import { ContentBlock, EditorState, SelectionState, genKey } from 'draft-js';

const isPicture = (block?: ContentBlock) => block?.getType() === 'atomic';

const caretAt = (block: ContentBlock, offset: number) =>
  SelectionState.createEmpty(block.getKey()).merge({ anchorOffset: offset, focusOffset: offset, hasFocus: true });

/**
 * The editor with its caret on a picture that was clicked, or null when the key is not a picture's. Clicking a picture
 * only opens react-draft-wysiwyg's alignment controls and leaves the caret wherever it was, so Delete then took a
 * letter out of another line instead of the picture (#33670).
 */
export const selectPicture = (editorState: EditorState, blockKey: string): EditorState | null => {
  const block: ContentBlock | undefined = editorState.getCurrentContent().getBlockForKey(blockKey);
  return isPicture(block) ? EditorState.forceSelection(editorState, caretAt(block, 0)) : null;
};

/**
 * Backspace or Delete next to, or on, a picture in the HTML editor (automations, Notification Center): the state with
 * the picture removed, or null to leave the key to draft-js. Draft-js's own Backspace does nothing on the line after a
 * picture and its Delete does nothing on a selected one, so a picture at the end of a message could not be removed
 * from the keyboard (#33670).
 *
 * The caret stays where it was when the picture was beside it; when the picture itself was selected it goes to the end
 * of the line above, or the start of the line below. A picture that was the only content leaves an empty line.
 */
export const removePicture = (editorState: EditorState, command: string): EditorState | null => {
  const selection = editorState.getSelection();
  if (!selection.isCollapsed() || (command !== 'backspace' && command !== 'delete')) return null;

  const content = editorState.getCurrentContent();
  const block: ContentBlock = content.getBlockForKey(selection.getAnchorKey());
  const offset = selection.getAnchorOffset();

  let picture: ContentBlock | undefined;
  if (isPicture(block)) picture = block;
  else if (command === 'backspace' && offset === 0) picture = content.getBlockBefore(block.getKey());
  else if (command === 'delete' && offset === block.getLength()) picture = content.getBlockAfter(block.getKey());
  if (!picture || !isPicture(picture)) return null;

  let blockMap = content.getBlockMap().delete(picture.getKey());
  let caret = selection;
  if (picture === block) {
    const before: ContentBlock | undefined = content.getBlockBefore(block.getKey());
    const after: ContentBlock | undefined = content.getBlockAfter(block.getKey());
    if (before) caret = caretAt(before, before.getLength());
    else if (after) caret = caretAt(after, 0);
    else {
      const emptyLine = new ContentBlock({ key: genKey(), type: 'unstyled', text: '', characterList: block.getCharacterList().clear() });
      blockMap = blockMap.set(emptyLine.getKey(), emptyLine);
      caret = caretAt(emptyLine, 0);
    }
  }

  const withoutPicture = content.merge({ blockMap, selectionBefore: selection, selectionAfter: caret });
  return EditorState.forceSelection(EditorState.push(editorState, withoutPicture, 'remove-range'), caret);
};
